// Pedidos (docs/108 B7): las NV de todos los canales (vendedor, call center, B2B, ruta, mesón) por
// estado, canal y vendedor, con «Mis pedidos» (el vendedor asociado al usuario). Si el usuario ve la
// logística, se suma lo PENDIENTE de despachar de cada NV confirmada (derivado por el backend: lo
// confirmado menos lo despachado) y si ya va en una OD.
import { DocumentosNota } from '../dte/DocumentosNota';
import { useState } from 'react';
import { useNotas, useVendedores } from '../api/consultas';
import type { NotaListada } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { decimal, fecha, pesos } from '../comun/formato';
import { useDisponibles } from '../logistica/datos';
import { useSesion } from '../sesion/SesionContexto';
import { SelectorBodega, useBodegas } from '../stock/PantallasStock';

const ESTADOS = ['CONFIRMADA', 'EN_APROBACION', 'BORRADOR', 'CERRADA', 'CERRADA_CORTA', 'ANULADA'];
const CANALES = ['VENDEDOR', 'CALL_CENTER', 'B2B', 'RUTA', 'MESON', 'POS'];

export function Pedidos({
  alAbrirNota,
}: {
  alAbrirNota: (notaId: string, numero: string) => void;
}) {
  const { empresa, usuario, puede } = useSesion();
  const empresaId = empresa!.id;
  const vendedores = useVendedores(empresaId);
  const mio = (vendedores.data ?? []).find((v) => v.usuario_id === usuario?.id) ?? null;
  const [estado, setEstado] = useState('CONFIRMADA');
  const [canal, setCanal] = useState('');
  const [vendedorId, setVendedorId] = useState('');
  const [pagina, setPagina] = useState(1);
  const bodegas = useBodegas(empresaId);
  const [bodega, setBodega] = useState('');
  const ubicacionId = bodega || bodegas.lista[0]?.id || '';
  const verLogistica = puede('logistica.ver') && estado === 'CONFIRMADA';
  const q = useNotas(empresaId, {
    estado: estado || undefined,
    canal: canal || undefined,
    vendedorId: vendedorId || undefined,
    // Los pedidos: la venta directa (punto de venta, factura o boleta directa) no es nota de venta.
    ventaDirecta: 'false',
    pagina,
  });
  const pendientes = useDisponibles(
    empresaId,
    verLogistica ? { ubicacionId, ...(vendedorId ? { vendedorId } : {}) } : { ubicacionId: '' },
  );
  const pend = new Map((pendientes.data ?? []).map((p) => [p.notaId, p]));
  const nombreVendedor = (id: string | null | undefined) => {
    const v = (vendedores.data ?? []).find((x) => x.id === id);
    return v ? `${v.codigo} · ${v.nombre}` : '';
  };
  const columnas: Columna<NotaListada & { vendedor_id?: string | null }>[] = [
    {
      id: 'n',
      titulo: 'N°',
      celda: (n) => <span className="mono">{n.numeroVisible}</span>,
      ancho: '8rem',
    },
    { id: 'f', titulo: 'Fecha', celda: (n) => fecha(n.fecha_negocio), ancho: '7rem' },
    { id: 'c', titulo: 'Cliente', celda: (n) => n.cliente_razon_social },
    {
      id: 'can',
      titulo: 'Canal',
      celda: (n) => n.canal.toLowerCase().replace('_', ' '),
      ancho: '7rem',
    },
    { id: 'v', titulo: 'Vendedor', celda: (n) => nombreVendedor(n.vendedor_id), ancho: '12rem' },
    {
      id: 'd',
      titulo: 'Documento',
      celda: (n) => (
        <DocumentosNota
          documentos={n.documentos}
          vacio={`${n.documento_solicitado.toLowerCase()} por emitir`}
        />
      ),
      ancho: '11rem',
    },
    ...(verLogistica
      ? ([
          {
            id: 'p',
            titulo: 'Pendiente',
            celda: (n) => {
              const p = pend.get(n.id);
              return p ? (
                `${decimal(p.unidadesPendientes)} un · ${p.lineasPendientes} lín.`
              ) : (
                <span className="apagado">cumplida</span>
              );
            },
            alinear: 'der',
            ancho: '10rem',
          },
          {
            id: 'b',
            titulo: 'Bruto',
            celda: (n) => pesos(pend.get(n.id)?.bruto ?? null),
            alinear: 'der',
            ancho: '8rem',
          },
          {
            id: 'od',
            titulo: 'ODD',
            celda: (n) => (pend.get(n.id)?.enOd ? `ODD ${pend.get(n.id)!.enOd}` : ''),
            ancho: '5rem',
          },
        ] as Columna<NotaListada>[])
      : []),
    {
      id: 'e',
      titulo: 'Estado',
      celda: (n) => n.estado.toLowerCase().replace('_', ' '),
      ancho: '8rem',
    },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="pedidos">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado del pedido"
            value={estado}
            onChange={(e) => {
              setEstado(e.target.value);
              setPagina(1);
            }}
          >
            <option value="">Todos</option>
            {ESTADOS.map((x) => (
              <option key={x} value={x}>
                {x.toLowerCase().replace('_', ' ')}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Canal</span>
          <select
            aria-label="Canal"
            value={canal}
            onChange={(e) => {
              setCanal(e.target.value);
              setPagina(1);
            }}
          >
            <option value="">Todos</option>
            {CANALES.map((x) => (
              <option key={x} value={x}>
                {x.toLowerCase().replace('_', ' ')}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Vendedor</span>
          <select
            aria-label="Vendedor"
            value={vendedorId}
            onChange={(e) => {
              setVendedorId(e.target.value);
              setPagina(1);
            }}
          >
            <option value="">Todos</option>
            {(vendedores.data ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.codigo} · {v.nombre}
              </option>
            ))}
          </select>
        </label>
        {mio && (
          <button
            type="button"
            className={vendedorId === mio.id ? 'primario' : undefined}
            onClick={() => setVendedorId(vendedorId === mio.id ? '' : mio.id)}
          >
            Mis pedidos
          </button>
        )}
        {verLogistica && (
          <SelectorBodega bodegas={bodegas.lista} valor={ubicacionId} alCambiar={setBodega} />
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Pedidos"
        columnas={columnas}
        filas={q.data?.items ?? []}
        clave={(n) => n.id}
        cargando={q.isLoading}
        vacio="Sin pedidos"
        alAbrir={(n) => alAbrirNota(n.id, n.numeroVisible)}
        paginacion={{ pagina, hayMas: (q.data?.items.length ?? 0) >= 50, alCambiar: setPagina }}
        autofoco
      />
    </div>
  );
}
